'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CTASectionProps {
  className?: string;
}

const CTASection = ({ className = '' }: CTASectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className={`py-16 lg:py-24 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative glassmorphic rounded-3xl overflow-hidden shadow-silver-glow-lg card-3d-glow">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(192, 192, 192, 0.3) 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}></div>
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10"></div>

          {/* Content */}
          <div className="relative z-10 px-8 py-12 lg:px-16 lg:py-20 text-center">
            <div className="max-w-4xl mx-auto space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 glassmorphic px-4 py-2 rounded-full shadow-silver">
                <Icon name="RocketLaunchIcon" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Start vandaag nog</span>
              </div>

              {/* Heading */}
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-bold leading-tight text-3d">
                Klaar om jouw bedrijf te{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-secondary">
                  transformeren met AI?
                </span>
              </h2>

              {/* Description */}
              <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
                Ontdek in een gratis AI-scan welke kansen er liggen voor jouw bedrijf. Geen verplichtingen, alleen concrete inzichten.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link
                  href="/contact"
                  className="group relative px-8 py-4 rounded-lg font-semibold text-lg bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover hover:-translate-y-2 w-full sm:w-auto button-3d"
                >
                  <span className="relative z-10 flex items-center justify-center space-x-2">
                    <span>Gratis AI-scan aanvragen</span>
                    <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cta to-error rounded-lg opacity-0 group-hover:opacity-100 transition-opacity blur-xl"></div>
                </Link>

                <Link
                  href="/pricing"
                  className="px-8 py-4 rounded-lg font-semibold text-lg border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover w-full sm:w-auto card-3d-glow"
                >
                  Bekijk pakketten
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 pt-8 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="ClockIcon" size={18} className="text-accent" />
                  <span>Implementatie in 4-8 weken</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="ShieldCheckIcon" size={18} className="text-success" />
                  <span>100% GDPR-compliant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CurrencyEuroIcon" size={18} className="text-warning" />
                  <span>Geen verborgen kosten</span>
                </div>
              </div>

              {/* Additional Info */}
              <div className="pt-8 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Heb je vragen? Neem direct contact op via{' '}
                  <a href="mailto:info@aimagency.nl" className="text-primary hover:text-accent transition-colors">
                    info@aimagency.nl
                  </a>
                  {' '}of bel{' '}
                  <a href="tel:+31201234567" className="text-primary hover:text-accent transition-colors">
                    +31 20 123 4567
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;